import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  Menu,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogActions,
  Switch,
  CircularProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import CloseIcon from "@mui/icons-material/Close";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import { toast, ToastContainer } from "react-toastify";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import {
  listChapters,
  listAllSubjects,
  createChapter,
  updateChapter,
  previewDelete,
  confirmDelete,
  apiErrorMessage,
} from "../../api/assessmentHierarchy";
import { indexAssessmentChapter } from "../../api/assessmentQuestions";
import useDebouncedValue from "./useDebouncedValue";
import AssessmentTableSkeleton from "./AssessmentTableSkeleton";
import AssessmentEmptyState from "./shell/AssessmentEmptyState";
import BulkImportButton from "./BulkImportButton";
const ACCENT = "#17479e";

const allColumns = [
  { key: "id", label: "ID", cellCss: { minWidth: "60px", width: "60px" }, getValue: (r) => r.id },
  { key: "title", label: "Name", cellCss: { minWidth: "220px" }, getValue: (r) => r.title },
  { key: "subject", label: "Subject", cellCss: { minWidth: "180px" }, getValue: (r) => r.subjectNames.join(", ") },
  { key: "topics", label: "Topics", cellCss: { align: "center", minWidth: "90px" }, getValue: (r) => r.topicCount },
  { key: "synopsis", label: "Synopsis", cellCss: { align: "center", minWidth: "100px" }, getValue: (r) => r.synopsisAvailable },
  { key: "questions", label: "Questions", cellCss: { align: "center", minWidth: "110px" }, getValue: (r) => r.questionCount },
  { key: "status", label: "Status", cellCss: { align: "center", minWidth: "120px" }, getValue: (r) => r.status },
];

const defaultVisibleKeys = new Set(allColumns.map((c) => c.key));
const STORAGE_KEY = "assessmentChaptersVisibleColumns";

const statusCfg = {
  ACTIVE: { label: "Active", color: "#2e7d32", bg: "#e8f5e9" },
  INACTIVE: { label: "Inactive", color: "#555", bg: "#f0f0f0" },
};

function StatusChip({ value }) {
  const cfg = statusCfg[value] ?? { label: value ?? "—", color: "#555", bg: "#f5f5f5" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.7rem", color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}
    />
  );
}

function ChapterFormDialog({ open, chapter, subjects, onClose, onSave }) {
  const [title, setTitle] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [subjectIds, setSubjectIds] = useState([]);
  const [order, setOrder] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      return;
    }
    setNameTouched(false);
    setTitle(chapter?.title || "");
    setDescription(chapter?.description || "");
    setSubjectIds(chapter?.subjectIds || []);
    setOrder("");
    setStatus(chapter?.status || "ACTIVE");
  }, [open, chapter]);

  const handleSubmit = async () => {
    if (!title.trim() || subjectIds.length === 0 || submitting) return;
    setSubmitting(true);
    try {
      const values = {
        id: chapter?.id,
        name: title.trim(),
        description: description.trim(),
        subjectIds,
        active: status === "ACTIVE",
      };
      if (order !== "") values.displayOrder = Number(order);
      await onSave(values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: "#f7f8fb", overflow: "hidden" } }}
    >
      <Box
        sx={{
          position: "relative",
          p: 2.5,
          background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
          color: "#fff",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 10, right: 10, color: "rgba(255,255,255,0.85)" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.18)",
            }}
          >
            <AutoStoriesOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>
              {chapter ? "Edit Chapter" : "Add Chapter"}
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>
              {chapter ? "Update name, subject and status" : "Name it, link a subject and set status"}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <DialogContent sx={{ pt: 3, backgroundColor: "#f7f8fb" }}>
        <Card sx={{ boxShadow: 1, borderRadius: 3, overflow: "hidden" }}>
          <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.25 }}>
            <TextField
              label="Chapter Name"
              fullWidth
              size="small"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => setNameTouched(true)}
              error={nameTouched && !title.trim()}
              helperText={nameTouched && !title.trim() ? "Please fill out this field" : undefined}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <FormControl fullWidth size="small" required>
              <InputLabel>Subjects</InputLabel>
              <Select
                multiple
                label="Subjects *"
                value={subjectIds}
                onChange={(e) => setSubjectIds(e.target.value)}
                renderValue={(selected) =>
                  selected.map((id) => subjects.find((s) => s.id === id)?.name ?? id).join(", ")
                }
              >
                {subjects.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    <Checkbox checked={subjectIds.includes(s.id)} size="small" />
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Description"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <TextField
              label="Display Order (optional)"
              type="number"
              fullWidth
              size="small"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: status === "ACTIVE" ? "#2e7d32" : "divider",
                backgroundColor: status === "ACTIVE" ? "#eaf7ec" : "#f5f7fb",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: status === "ACTIVE" ? "#2e7d32" : "#555" }}>
                  {status === "ACTIVE" ? "Active" : "Inactive"}
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                  {status === "ACTIVE" ? "Visible to students" : "Hidden from students"}
                </Typography>
              </Box>
              <Switch
                checked={status === "ACTIVE"}
                onChange={(e) => setStatus(e.target.checked ? "ACTIVE" : "INACTIVE")}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#2e7d32" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#2e7d32" },
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, backgroundColor: "#f7f8fb" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 1.5 }}>
          <Button
            onClick={onClose}
            disabled={submitting}
            sx={{
              flex: "0 0 auto",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              color: "#555",
              backgroundColor: "#eceff1",
              "&:hover": { backgroundColor: "#e0e3e6" },
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disableElevation
            loading={submitting}
            onClick={handleSubmit}
            disabled={!title.trim() || subjectIds.length === 0}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
              "&:hover": { background: `linear-gradient(135deg, #123a80 0%, #0d47a1 100%)` },
              "&.Mui-disabled": { background: "#b0bec5" },
            }}
          >
            {chapter ? "Save Changes" : "Create Chapter"}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function AssessmentChapters() {
  const [chapters, setChapters] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [subjectFilter, setSubjectFilter] = useState("");
  const [publishFilter, setPublishFilter] = useState("");
  const [orderBy, setOrderBy] = useState("title");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [indexingId, setIndexingId] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const validKeys = new Set(allColumns.map((c) => c.key));
          const filtered = arr.filter((k) => validKeys.has(k));
          if (filtered.length > 0) return new Set(filtered);
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  const loadChapters = () => {
    setLoading(true);
    listChapters({
      page,
      size: rowsPerPage,
      search: debouncedSearch,
      subjectId: subjectFilter,
      isActive: publishFilter ? publishFilter === "ACTIVE" : undefined,
    })
      .then((data) => {
        setChapters(data.content);
        setTotal(data.totalElements ?? data.content.length);
      })
      .catch((err) => {
        setChapters([]);
        setTotal(0);
        toast.error(apiErrorMessage(err, "Failed to load chapters"));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadChapters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, subjectFilter, publishFilter, page, rowsPerPage]);

  useEffect(() => {
    listAllSubjects()
      .then(setSubjects)
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load subjects")));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(visibleColumns)));
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));

  const sortedData = useMemo(() => {
    return [...chapters].sort((a, b) => {
      // Sorting is client-side, within the current page only.
      const aValue = orderBy === "subject" ? a.subjectNames.join(", ") : a[orderBy];
      const bValue = orderBy === "subject" ? b.subjectNames.join(", ") : b[orderBy];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "string") {
        return order === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
  }, [chapters, orderBy, order]);

  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, subjectFilter, publishFilter]);

  const paginatedData = sortedData;

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (chapter) => {
    setEditing(chapter);
    setFormOpen(true);
  };

  const handleSave = ({ id, ...payload }) => {
    const request = id ? updateChapter(id, payload) : createChapter(payload);
    return request
      .then(() => {
        toast.success(id ? "Chapter updated successfully!" : "Chapter created successfully!");
        setFormOpen(false);
        loadChapters();
      })
      .catch((err) => {
        toast.error(apiErrorMessage(err, "Failed to save chapter"));
      });
  };

  // Same manual "Index Process" the books page has: (re)indexes one chapter's synopsis for RAG.
  const handleIndexChapter = async (row) => {
    setIndexingId(row.id);
    try {
      await indexAssessmentChapter(row.id);
      toast.success("Chapter synopsis indexing started!", { autoClose: 3000 });
    } catch (err) {
      toast.error(apiErrorMessage(err, "Error indexing chapter. Please try again."));
    } finally {
      setIndexingId(null);
    }
  };

  // Step 1 of the two-step delete: ask the backend what would be removed. A
  // guard failure (e.g. still referenced by questions) is shown as a toast and
  // no dialog opens.
  const handleDeleteClick = (row) => {
    previewDelete("chapter", row.id)
      .then((preview) => {
        setDeleteId(row.id);
        setDeleteMessage(preview?.message || "This action cannot be undone.");
      })
      .catch((err) => toast.error(apiErrorMessage(err, "This chapter cannot be deleted")));
  };

  const handleDelete = () => {
    confirmDelete("chapter", deleteId)
      .then((result) => {
        toast.success(result?.message || "Chapter deleted!");
        setDeleteId(null);
        loadChapters();
      })
      .catch((err) => {
        setDeleteId(null);
        toast.error(apiErrorMessage(err, "Failed to delete chapter"));
      });
  };

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />

      <Card sx={{ boxShadow: 1, mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flex: "1 1 560px", alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search chapters"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 220 }}
              />
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Subject</InputLabel>
                <Select label="Subject" value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)}>
                  <MenuItem value="">All subjects</MenuItem>
                  {subjects.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={publishFilter} onChange={(e) => setPublishFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={handleOpenColumnsMenu}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Columns
              </Button>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={openColumnsMenu}
                onClose={handleCloseColumnsMenu}
                PaperProps={{ sx: { width: 200, maxHeight: 500, overflowY: "auto", borderRadius: 2 } }}
              >
                {allColumns.map((col) => (
                  <FormControlLabel
                    key={col.key}
                    control={<Checkbox checked={visibleColumns.has(col.key)} onChange={() => toggleColumn(col.key)} />}
                    label={col.label}
                    sx={{ pl: 1, pr: 2 }}
                  />
                ))}
              </Menu>
              <BulkImportButton onImported={loadChapters} />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={openAdd}
                {...BUTTON_STYLES.primary}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Add Chapter
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 2, borderRadius: 3 }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 300px)", minHeight: 420 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {visibleColumnDefs.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.cellCss?.align}
                    sx={{
                      ...c.cellCss,
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                    sortDirection={orderBy === c.key ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === c.key}
                      direction={orderBy === c.key ? order : "asc"}
                      onClick={() => handleSort(c.key)}
                      sx={{
                        color: "#ffffff !important",
                        "&.Mui-active": { color: "#ffffff !important" },
                        "& .MuiTableSortLabel-icon": { color: "#ffffff !important" },
                      }}
                    >
                      {c.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    minWidth: "140px",
                    padding: "10px 12px",
                    backgroundColor: ACCENT,
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    right: 0,
                    zIndex: 4,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <AssessmentTableSkeleton columns={visibleColumnDefs.length + 1} />
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnDefs.length + 1} sx={{ textAlign: "center", py: 4 }}>
                    <AssessmentEmptyState message="No chapters found" hint="Try changing your search or filters." />
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ "&:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.02)" } }}
                  >
                    {visibleColumnDefs.map((c) => (
                      <TableCell key={c.key} align={c.cellCss?.align} sx={{ padding: "10px 12px", ...c.cellCss }}>
                        {(() => {
                          if (c.key === "id") {
                            return (
                              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>{row.id}</Typography>
                            );
                          }
                          if (c.key === "title") {
                            return <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{row.title}</Typography>;
                          }
                          if (c.key === "subject") return row.subjectNames.join(", ") || "—";
                          if (c.key === "status") return <StatusChip value={row.status} />;
                          if (c.key === "synopsis") return row.synopsisAvailable ? "Yes" : "No";
                          return c.getValue(row);
                        })()}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{ padding: "10px 12px", position: "sticky", right: 0, backgroundColor: "#fff", zIndex: 1 }}
                    >
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {row.synopsisAvailable && (
                          <Tooltip title="Index Synopsis" arrow placement="top">
                            <span>
                              <IconButton
                                size="small"
                                color="secondary"
                                disabled={indexingId === row.id}
                                onClick={() => handleIndexChapter(row)}
                                sx={{ "&:hover": { backgroundColor: "secondary.light", color: "white" } }}
                              >
                                {indexingId === row.id ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <RefreshIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit Chapter" arrow placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEdit(row)}
                            sx={{ "&:hover": { backgroundColor: "primary.light", color: "white" } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Chapter" arrow placement="top">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(row)}
                            sx={{ "&:hover": { backgroundColor: "error.light", color: "white" } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": { paddingLeft: 2, paddingRight: 2 },
          }}
        />
      </Paper>

      <ChapterFormDialog
        open={formOpen}
        chapter={editing}
        subjects={subjects}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />

      <DeleteConfirmationDialog
        open={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message={deleteMessage}
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default AssessmentChapters;
